import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import { useAutoRefresh } from '@/lib/hooks/use-auto-refresh'
import { fraudClient, type FraudAssessmentsParams, type FraudUsersParams } from './fraud-client'
import type { CreateChargebackInput, CreateFraudAllowanceInput, CreateFraudBlockInput, DisputeDraft, FraudPlatformSettings, FraudPolicyOverrides } from './fraud-types'

// Live-ish data: the overview and feed refresh every minute on their own and
// every 10 s when the header's auto-refresh toggle is on.
const LIVE_INTERVAL = 60_000

export const fraudKeys = {
  all: (eventId: number) => ['fraud', eventId] as const,
  overview: (eventId: number, window: string) => ['fraud', eventId, 'overview', window] as const,
  users: (eventId: number, params?: FraudUsersParams) => ['fraud', eventId, 'users', params] as const,
  user: (eventId: number, userId: number) => ['fraud', eventId, 'user', userId] as const,
  card: (eventId: number, instrumentId: string) => ['fraud', eventId, 'card', instrumentId] as const,
  assessments: (eventId: number, params?: FraudAssessmentsParams) => ['fraud', eventId, 'assessments', params] as const,
  policy: (eventId: number) => ['fraud', eventId, 'policy'] as const,
  blocks: (eventId: number, params?: { active?: boolean; user?: number }) => ['fraud', eventId, 'blocks', params] as const,
  allowances: (eventId: number, params?: { active?: boolean; user?: number }) => ['fraud', eventId, 'allowances', params] as const,
  chargebacks: (eventId: number, params?: { user?: number; state?: string }) => ['fraud', eventId, 'chargebacks', params] as const,
  reloads: (eventId: number, q: string, limit?: number) => ['fraud', eventId, 'reloads', q, limit] as const,
}

function useLiveInterval() {
  const { refetchInterval } = useAutoRefresh()
  return refetchInterval || LIVE_INTERVAL
}

export function useFraudOverview(eventId: number, window = '60m') {
  const { token } = useAuth()
  const refetchInterval = useLiveInterval()
  return useQuery({
    queryKey: fraudKeys.overview(eventId, window),
    queryFn: () => fraudClient.getOverview(token!, eventId, { window }),
    enabled: !!token && !!eventId,
    refetchInterval,
    // Keep the last numbers on screen while a new window loads.
    placeholderData: (previous) => previous,
  })
}

export function useFraudUsers(eventId: number, params?: FraudUsersParams) {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudKeys.users(eventId, params),
    queryFn: () => fraudClient.getUsers(token!, eventId, params),
    enabled: !!token && !!eventId,
    // Keep the list on screen while a search or page loads.
    placeholderData: (previous) => previous,
  })
}

export function useFraudUser(eventId: number, userId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudKeys.user(eventId, userId),
    queryFn: () => fraudClient.getUser(token!, eventId, userId),
    enabled: !!token && !!eventId && !!userId,
  })
}

export function useFraudCard(eventId: number, instrumentId: string, enabled = true) {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudKeys.card(eventId, instrumentId),
    queryFn: () => fraudClient.getCard(token!, eventId, instrumentId),
    enabled: !!token && !!eventId && !!instrumentId && enabled,
    staleTime: 15_000,
  })
}

export function useFraudAssessments(eventId: number, params?: FraudAssessmentsParams) {
  const { token } = useAuth()
  const refetchInterval = useLiveInterval()
  return useQuery({
    queryKey: fraudKeys.assessments(eventId, params),
    queryFn: () => fraudClient.getAssessments(token!, eventId, params),
    enabled: !!token && !!eventId,
    refetchInterval,
  })
}

export function useFraudPolicy(eventId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudKeys.policy(eventId),
    queryFn: () => fraudClient.getPolicy(token!, eventId),
    enabled: !!token && !!eventId,
  })
}

export function useUpdateFraudPolicy(eventId: number) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<FraudPolicyOverrides>) => fraudClient.updatePolicy(token!, eventId, data),
    onSuccess: (response) => {
      queryClient.setQueryData(fraudKeys.policy(eventId), response)
      // The overview carries the effective policy and the ranking's
      // decisions depend on it.
      queryClient.invalidateQueries({ queryKey: fraudKeys.all(eventId) })
    },
  })
}

export function useFraudBlocks(eventId: number, params?: { active?: boolean; user?: number }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudKeys.blocks(eventId, params),
    queryFn: () => fraudClient.getBlocks(token!, eventId, params),
    enabled: !!token && !!eventId,
  })
}

export function useCreateFraudBlock(eventId: number) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateFraudBlockInput) => fraudClient.createBlock(token!, eventId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fraudKeys.all(eventId) }),
  })
}

export function useLiftFraudBlock(eventId: number) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ blockId, notes }: { blockId: number; notes: string }) => fraudClient.liftBlock(token!, blockId, notes),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fraudKeys.all(eventId) }),
  })
}

export function useFraudAllowances(eventId: number, params?: { active?: boolean; user?: number }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudKeys.allowances(eventId, params),
    queryFn: () => fraudClient.getAllowances(token!, eventId, params),
    enabled: !!token && !!eventId,
  })
}

export function useCreateFraudAllowance(eventId: number) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateFraudAllowanceInput) => fraudClient.createAllowance(token!, eventId, data),
    // Trust changes decisions on the ranking and the overview too.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fraudKeys.all(eventId) }),
  })
}

export function useRevokeFraudAllowance(eventId: number) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (allowanceId: number) => fraudClient.revokeAllowance(token!, allowanceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fraudKeys.all(eventId) }),
  })
}

export function useFraudReloads(eventId: number, q: string, enabled = true, limit?: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudKeys.reloads(eventId, q, limit),
    queryFn: () => fraudClient.searchReloads(token!, eventId, q, limit),
    enabled: !!token && !!eventId && enabled,
    staleTime: 15_000,
    // Keep the previous list on screen while the next keystroke loads.
    placeholderData: (previous) => previous,
  })
}

export function useFraudChargebacks(eventId: number, params?: { user?: number; state?: string }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudKeys.chargebacks(eventId, params),
    queryFn: () => fraudClient.getChargebacks(token!, eventId, params),
    enabled: !!token && !!eventId,
  })
}

export function useCreateChargeback(eventId: number) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateChargebackInput) => fraudClient.createChargeback(token!, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fraudKeys.all(eventId) }),
  })
}

export function useUpdateChargeback(eventId: number) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ chargebackId, data }: { chargebackId: number; data: Parameters<typeof fraudClient.updateChargeback>[2] }) =>
      fraudClient.updateChargeback(token!, chargebackId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: fraudKeys.all(eventId) }),
  })
}

// ---------------------------------------------------------------------------
// EventPay staff (Admin → Fraud)
// ---------------------------------------------------------------------------

export const fraudAdminKeys = {
  all: ['fraud-admin'] as const,
  platform: () => ['fraud-admin', 'platform'] as const,
  events: (params?: { window?: string; scope?: string; q?: string }) => ['fraud-admin', 'events', params] as const,
  users: (q: string) => ['fraud-admin', 'users', q] as const,
  profile: (userId: number) => ['fraud-admin', 'profile', userId] as const,
  blocks: (params?: { active?: boolean; user?: number }) => ['fraud-admin', 'blocks', params] as const,
  chargebacks: (params?: { state?: string; user?: number }) => ['fraud-admin', 'chargebacks', params] as const,
  dispute: (chargebackId: number) => ['fraud-admin', 'dispute', chargebackId] as const,
}

function useInvalidateEverything() {
  const queryClient = useQueryClient()
  // Staff actions change what every event page shows too.
  return () => {
    queryClient.invalidateQueries({ queryKey: fraudAdminKeys.all })
    queryClient.invalidateQueries({ queryKey: ['fraud'] })
  }
}

export function useFraudPlatform() {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudAdminKeys.platform(),
    queryFn: () => fraudClient.getPlatform(token!),
    enabled: !!token,
  })
}

export function useUpdateFraudPlatform() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = useInvalidateEverything()
  return useMutation({
    mutationFn: (data: Partial<Pick<FraudPlatformSettings, 'mode' | 'default_profile' | 'notes'>>) => fraudClient.updatePlatform(token!, data),
    onSuccess: (response) => {
      queryClient.setQueryData(fraudAdminKeys.platform(), response)
      invalidate()
    },
  })
}

export function useFraudAdminEvents(params?: { window?: string; scope?: 'active' | 'all'; q?: string }) {
  const { token } = useAuth()
  const refetchInterval = useLiveInterval()
  return useQuery({
    queryKey: fraudAdminKeys.events(params),
    queryFn: () => fraudClient.getAdminEvents(token!, params),
    enabled: !!token,
    refetchInterval,
    placeholderData: (previous) => previous,
  })
}

export function useFraudAdminUsers(q: string, enabled = true) {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudAdminKeys.users(q),
    queryFn: () => fraudClient.searchAdminUsers(token!, q),
    enabled: !!token && enabled,
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  })
}

export function useFraudUserProfile(userId: number) {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudAdminKeys.profile(userId),
    queryFn: () => fraudClient.getUserProfile(token!, userId),
    enabled: !!token && !!userId,
  })
}

export function useFraudAllBlocks(params?: { active?: boolean; user?: number }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudAdminKeys.blocks(params),
    queryFn: () => fraudClient.getAllBlocks(token!, params),
    enabled: !!token,
  })
}

export function useAdminCreateBlock() {
  const { token } = useAuth()
  const invalidate = useInvalidateEverything()
  return useMutation({
    mutationFn: (data: CreateFraudBlockInput) => fraudClient.createGlobalBlock(token!, data),
    onSuccess: invalidate,
  })
}

export function useAdminLiftBlock() {
  const { token } = useAuth()
  const invalidate = useInvalidateEverything()
  return useMutation({
    mutationFn: ({ blockId, notes }: { blockId: number; notes: string }) => fraudClient.liftBlock(token!, blockId, notes),
    onSuccess: invalidate,
  })
}

export function useFraudAllChargebacks(params?: { state?: string; user?: number }) {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudAdminKeys.chargebacks(params),
    queryFn: () => fraudClient.getAllChargebacks(token!, params),
    enabled: !!token,
  })
}

export function useAdminUpdateChargeback() {
  const { token } = useAuth()
  const invalidate = useInvalidateEverything()
  return useMutation({
    mutationFn: ({ chargebackId, data }: { chargebackId: number; data: Parameters<typeof fraudClient.updateChargeback>[2] }) =>
      fraudClient.updateChargeback(token!, chargebackId, data),
    onSuccess: invalidate,
  })
}

export function useDispute(chargebackId: number | null) {
  const { token } = useAuth()
  return useQuery({
    queryKey: fraudAdminKeys.dispute(chargebackId ?? 0),
    queryFn: () => fraudClient.getDispute(token!, chargebackId!),
    enabled: !!token && !!chargebackId,
  })
}

/** A dispute action: the detail is replaced by the answer, and every list refetches. */
function useDisputeMutation<TInput, TResult extends DisputeDraft>(
  call: (token: string, chargebackId: number, data: TInput) => Promise<TResult>,
) {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const invalidate = useInvalidateEverything()
  return useMutation({
    mutationFn: ({ chargebackId, data }: { chargebackId: number; data: TInput }) => call(token!, chargebackId, data),
    onSuccess: (dispute) => {
      queryClient.setQueryData(fraudAdminKeys.dispute(dispute.id), dispute)
      invalidate()
    },
  })
}

export const useUpdateDispute = () => useDisputeMutation(fraudClient.updateDispute)
export const useRedraftDispute = () => useDisputeMutation((token: string, id: number) => fraudClient.redraftDispute(token, id))
export const useSubmitDispute = () => useDisputeMutation(fraudClient.submitDispute)
export const useAcceptDispute = () => useDisputeMutation(fraudClient.acceptDispute)
